import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { isBeforeFirstSpecialGamesSpin } from '@Core/SpecialGames/SpecialGamesHelper';
import { CycleStep, CycleStepType, ScatterCycleStep, WaysCycleStep, WinlineCycleStep } from '@Core/SymbolsHighlighter/SymbolsHighlighter.types';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EWinType } from '@Managers/Redux/HttpState';
import { EGameStates } from '@Managers/StateManager/GameStates';
import i18next from 'i18next';

export class GamePlayMessages extends GameComponent {
    private lastStep?: CycleStep;
    private hasSpecialGameEnded: boolean = false;

    constructor(scene: GameScene) {
        super(scene);

        EventsCenter.on(EGameStates.PRE_WIN_STATE, this.onPreWin, this);
        EventsCenter.on(EGameStates.POST_WIN_STATE, this.onPostWin, this);
        EventsCenter.on(EGameStates.POST_WIN_STEP_STATE, this.onPostWinStepState, this);
        EventsCenter.on(EGameStates.SPIN_STATE, this.onSpin, this);
        EventsCenter.on(EGameEvents.CYCLE_WAYS, this.onCycle, this);
        EventsCenter.on(EGameEvents.CYCLE_WINLINE, this.onCycle, this);
        EventsCenter.on(EGameEvents.CYCLE_SCATTERS, this.onCycle, this);
        EventsCenter.on(EGameStates.COMPLETE_WIN_STATE, this.onCompleteWin, this);
        EventsCenter.on(EGameStates.GAMBLE_RESULT_STATE, this.onGambleResult, this);
        EventsCenter.on(EGameStates.POST_GAMBLE_STATE, this.onPostGamble, this);
        EventsCenter.on(EGameStates.IDLE_STATE, this.onIdle, this);
        EventsCenter.on(EGameStates.PRE_GAMBLE_STATE, this.onPreGamble, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_TRIGGER, this.onSpecialGamesTrigger, this);
        EventsCenter.on(EGameStates.PRE_WIN_SPECIAL_GAMES_RETRIGGER, this.onSpecialGamesTrigger, this);
        EventsCenter.on(EGameEvents.SPECIAL_GAMES_RETRIGGER_SHOW, this.onSpecialGamesTrigger, this);
        EventsCenter.on(EGameStates.PRE_SPECIAL_GAMES_INTRO_STATE, this.onPreSpecialGamesIntroState, this);
    }

    onInit(): void {
        this.gameStore.on(EGameStoreKeys.CurrentSpecialGamesType, ({ value: currentSpecialGamesType, previousValue: previousSpecialGamesType }) => {
            if (previousSpecialGamesType !== undefined && previousSpecialGamesType !== currentSpecialGamesType) this.hasSpecialGameEnded = true;
        });
    }

    public onCreate(): void {
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: '' }, true);
    }

    private onPreWin(): void {
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: '', information: '' }, true);
    }

    private onPostWin(): void {
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, {}, true);

        const { win, limitReached } = this.gameStore.get(EGameStoreKeys.SpinOutcome);
        if (!win) return;

        const winMessage = `${i18next.t('WIN_LABEL')} ${CurrencyUtils.formatRawCurrencyValue(win)}`;
        const limitReachedMessage = `${i18next.t('WIN_LIMIT_REACHED', { winLimit: CurrencyUtils.formatRawCurrencyValue(win) })}`;
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: limitReached ? limitReachedMessage : winMessage }, true);

        if (this.lastStep) this.updateCycleMessage(this.lastStep);
    }

    private onPostWinStepState(): void {
        const { isLastWinPresentationStep } = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        if (isLastWinPresentationStep) return;
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { information: '' }, true);
    }

    private onSpin(): void {
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { information: i18next.t('GOOD_LUCK') }, true);
    }

    private onCycle(step: CycleStep): void {
        this.lastStep = step;
        this.updateCycleMessage(step);
    }

    private onCompleteWin(): void {
        const spinOutcome = this.gameStore.get(EGameStoreKeys.SpinOutcome);
        let lastWinValue: number | undefined = spinOutcome.win;
        if (this.hasSpecialGameEnded) {
            if (spinOutcome.totalWin) lastWinValue = spinOutcome.totalWin;
            this.hasSpecialGameEnded = false;
        }

        if (!lastWinValue || this.gameStore.get(EGameStoreKeys.GambleState).gambleRequested) return;

        if (isBeforeFirstSpecialGamesSpin(this.gameStore)) return;

        const lastWinMessage = `${i18next.t('LAST_WIN_LABEL')} ${CurrencyUtils.formatRawCurrencyValue(lastWinValue)}`;
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: lastWinMessage }, true);
    }

    private onPreSpecialGamesIntroState() {
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: '' }, true);
    }

    private onPostGamble(): void {
        const gambleWin = this.gameStore.get(EGameStoreKeys.GambleResponse).gamble?.amount;
        const gambleTries = this.gameStore.get(EGameStoreKeys.GambleState).tries;
        const lastWinValue = this.gameStore.get(EGameStoreKeys.SpinOutcome).win;
        if (!gambleTries && lastWinValue) {
            const lastWinMessage = `${i18next.t('LAST_WIN_LABEL')} ${CurrencyUtils.formatRawCurrencyValue(lastWinValue)}`;
            this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: lastWinMessage }, true);
        } else if (gambleTries && gambleWin) {
            const lastWinMessage = `${i18next.t('LAST_WIN_LABEL')} ${CurrencyUtils.formatRawCurrencyValue(gambleWin)}`;
            this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: lastWinMessage }, true);
        }
    }

    private onGambleResult(): void {
        const gambleWin = this.gameStore.get(EGameStoreKeys.GambleResponse).gamble?.amount;
        if (!gambleWin) {
            this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: '' }, true);
        } else {
            const winMessage = `${i18next.t('WIN_LABEL')} ${CurrencyUtils.formatRawCurrencyValue(gambleWin)}`;
            this.gameStore.set(EGameStoreKeys.GamePlayMessages, { winning: winMessage }, true);
        }
    }

    private onIdle(): void {
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { information: i18next.t('PRESS_PLAY_TO_BEGIN') }, true);
    }

    private onPreGamble(): void {
        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { information: '' }, true);
    }

    private onSpecialGamesTrigger(): void {
        const currentScatterStep = (this.scene as GameScene).symbolHighlighter.getCurrentCycleSteps().find((step) => step.type === CycleStepType.SCATTER);
        const freeGamesWin = this.gameStore.get(EGameStoreKeys.FreeGames).spinsWon;
        const specialGamesWin = freeGamesWin ? this.formatSpecialGamesWinMessage({ fgWin: freeGamesWin }) : '';

        if (currentScatterStep) {
            this.lastStep = currentScatterStep;
            const message = this.buildCycleMessage(currentScatterStep);
            if (!message) return;

            this.gameStore.set(EGameStoreKeys.GamePlayMessages, { information: message }, true);
        } else if (specialGamesWin) {
            this.gameStore.set(EGameStoreKeys.GamePlayMessages, { information: specialGamesWin }, true);
        }
    }

    private updateCycleMessage(step: CycleStep): void {
        const { finalWinLevelName, isLastWinPresentationStep } = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        const isBigWin = finalWinLevelName !== EWinType.NORMAL_WIN;
        const isPostWinState = this.gameStore.get(EGameStoreKeys.CurrentGameState) === EGameStates.POST_WIN_STATE;

        if (isLastWinPresentationStep && isBigWin && !isPostWinState) return;

        const message = this.buildCycleMessage(step);
        if (!message) return;

        this.gameStore.set(EGameStoreKeys.GamePlayMessages, { information: message }, true);
    }

    private buildCycleMessage(step: CycleStep): string | null {
        switch (step.type) {
            case CycleStepType.SCATTER:
                return this.buildScatterMessage(step);
            case CycleStepType.MAIN:
                return 'paylineId' in step ? this.buildWinlineMessage(step) : this.buildWaysMessage(step);
        }
    }

    private buildScatterMessage(step: ScatterCycleStep): string | null {
        const { currentWinPresentationStepIndex } = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        const data = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps.at(currentWinPresentationStepIndex)?.scatters?.[step.index];

        if (!data?.win || !data?.positions) return null;

        const freeGamesWin = this.gameStore.get(EGameStoreKeys.FreeGames).spinsWon;
        const symbolConfig = this.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((s) => s.id === data.symbolId)!;

        const symbolsCount = data.positions.length;
        const symbolName = symbolConfig.symbolName;
        const win = data.win;
        const multiplier = step.multiplier;
        const showMultiplier = !!multiplier && multiplier > 1;

        return this.formatScatterMessage({ symbolsCount, symbolName, win, multiplier, showMultiplier, freeGamesWin });
    }

    private buildWaysMessage(step: WaysCycleStep): string | null {
        const { currentWinPresentationStepIndex } = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState);

        const winStep = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps.at(currentWinPresentationStepIndex);
        const data = winStep?.powerLines?.[step.index];
        if (!data) return null;
        const { numberOfReels, numberOfLines, win } = data;
        const symbolName = this.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((s) => s.id === data.symbolId)!.symbolName;
        const multiplier = step.multiplier;
        const showMultiplier = !!multiplier && multiplier > 1;
        return this.formatWaysMessage({
            numberOfReels,
            numberOfLines,
            symbolName,
            win,
            multiplier,
            showMultiplier
        });
    }

    private buildWinlineMessage(step: WinlineCycleStep): string | null {
        const { currentWinPresentationStepIndex } = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState);
        const winStep = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps.at(currentWinPresentationStepIndex);
        const winlineData = winStep?.winningLines?.[step.index];
        if (!winlineData) return null;
        const symbolConfigs = this.gameStore.get(EGameStoreKeys.SymbolConfiguration);
        const { id: lineIndex, symbolsCount, win } = winlineData;
        const multiplier = step.multiplier;
        const showMultiplier = !!multiplier && multiplier > 1;
        const { symbolId } = step;
        const symbolName = symbolConfigs.find((s) => s.id === symbolId)?.symbolName;
        if (!symbolName) return null;
        return this.formatWinlineMessage({
            lineIndex,
            symbolsCount,
            symbolName,
            win,
            multiplier,
            showMultiplier
        });
    }

    private formatScatterMessage(params: {
        symbolsCount: number;
        symbolName: string;
        win: number;
        multiplier?: number;
        showMultiplier: boolean;
        freeGamesWin: number;
    }): string {
        const { symbolsCount, symbolName, win, multiplier, showMultiplier, freeGamesWin } = params;
        const specialGamesWin = freeGamesWin ? this.formatSpecialGamesWinMessage({ fgWin: freeGamesWin }) : '';

        return (
            `${symbolsCount}x ${i18next.t(symbolName)} ` +
            `${showMultiplier && multiplier ? ' x' + multiplier : ''}` +
            ` = ${CurrencyUtils.formatRawCurrencyValue(win)}` +
            specialGamesWin
        );
    }

    private formatWinlineMessage(params: {
        lineIndex: number;
        symbolsCount: number;
        symbolName: string;
        win: number;
        multiplier?: number;
        showMultiplier: boolean;
    }): string {
        const { lineIndex, symbolsCount, symbolName, win, multiplier, showMultiplier } = params;

        return (
            `${i18next.t('LINE')} ${lineIndex + 1}: ` +
            `${symbolsCount}x ${i18next.t(symbolName)}` +
            `${showMultiplier && multiplier ? ' x' + multiplier : ''}` +
            ` = ${CurrencyUtils.formatRawCurrencyValue(win)}`
        );
    }

    private formatWaysMessage(params: {
        numberOfReels: number;
        numberOfLines: number;
        symbolName: string;
        win: number;
        multiplier?: number;
        showMultiplier: boolean;
    }): string {
        const { numberOfReels, symbolName, numberOfLines, win, multiplier, showMultiplier } = params;

        return (
            `${numberOfReels}x ${i18next.t(symbolName)} ` +
            `x ${numberOfLines} ${i18next.t('WAYS')}` +
            `${showMultiplier && multiplier ? ' x' + multiplier : ''}` +
            ` = ${CurrencyUtils.formatRawCurrencyValue(win)}`
        );
    }

    private formatSpecialGamesWinMessage(params: { fgWin: number }): string {
        //TODO: Add more types of special games
        const { fgWin } = params;

        const textVariables = { ...this.gameStore.get(EGameStoreKeys.TextVariables) };
        const texts = [];

        if (fgWin > 0) texts.push('T_FREEGAMES_INTRO_TEXT_1', 'T_FREEGAMES_INTRO_TEXT_2', 'T_FREEGAMES_INTRO_TEXT_3');

        let finalText = '';

        texts.forEach((text, index) => {
            finalText += ' ';
            finalText += i18next.t(text, { ...textVariables });
        });

        return finalText;
    }
}
